// ---------------------------------------------------------------------------
// TankWise domain data + solver — faithfully ported from the FastAPI backend
// (app/optimizer/model.py, app/optimizer/costs.py, app/ai/*, seed CSVs).
// Runs the same assignment optimization entirely in the browser.
// ---------------------------------------------------------------------------

// Cargo catalogue (subset of backend seed with full cleaning-matrix coverage)
export const CARGOES = [
  { key: "methanol",       en: "Methanol",       ja: "メタノール",         group: "alcohol" },
  { key: "ethanol",        en: "Ethanol",        ja: "エタノール",         group: "alcohol" },
  { key: "isopropanol",    en: "Isopropanol",    ja: "イソプロパノール",   group: "alcohol" },
  { key: "ethylene_glycol",en: "Ethylene glycol",ja: "エチレングリコール", group: "glycol"  },
  { key: "vegetable_oil",  en: "Vegetable oil",  ja: "植物油",             group: "oil"     },
  { key: "palm_oil",       en: "Palm oil",       ja: "パーム油",           group: "oil"     },
  { key: "toluene",        en: "Toluene",        ja: "トルエン",           group: "aromatic"},
  { key: "xylene",         en: "Xylene",         ja: "キシレン",           group: "aromatic"},
  { key: "caustic_soda",   en: "Caustic soda",   ja: "苛性ソーダ",         group: "caustic" },
  { key: "sulphuric_acid", en: "Sulphuric acid", ja: "硫酸",               group: "acid"    },
  { key: "phosphoric_acid",en: "Phosphoric acid",ja: "リン酸",             group: "acid"    },
];

export const CARGO = Object.fromEntries(CARGOES.map((c) => [c.key, c]));

export const GROUP_COLOR = {
  alcohol:  "#8fd9c9",
  glycol:   "#7fc8e8",
  oil:      "#ffd27a",
  aromatic: "#c3b5f5",
  caustic:  "#9fb4dd",
  acid:     "#ff9d92",
};

// IBC compatibility rules (backend seed/ibc_rules.csv, filtered to catalogue)
// relation ∈ reactive | forbidden_same_tank | forbidden_adjacent
export const RULES = [
  ["caustic_soda", "sulphuric_acid",  "reactive"],
  ["caustic_soda", "phosphoric_acid", "reactive"],
  ["sulphuric_acid", "toluene",       "forbidden_adjacent"],
  ["sulphuric_acid", "xylene",        "forbidden_adjacent"],
];

// Contamination severity / probability — app/ai/contamination_scorer.py
const SEVERITY = { high: 250000, medium: 80000, low: 15000 };

export function relation(a, b) {
  if (a === b) return null;
  for (const [x, y, r] of RULES) {
    if ((x === a && y === b) || (x === b && y === a)) return r;
  }
  return null;
}

// Cleaning matrix (backend seed/cleaning_matrix.csv). Key "from>to".
// [chem_cost_usd, est_hours, requires_wall_wash, heating_required]
const M = {
  "methanol>methanol": [500, 2, 0, 0], "methanol>ethanol": [800, 4, 0, 0],
  "methanol>vegetable_oil": [1200, 8, 0, 0], "methanol>palm_oil": [1200, 8, 0, 0],
  "methanol>caustic_soda": [3500, 12, 1, 0], "methanol>toluene": [2000, 8, 0, 0],
  "methanol>isopropanol": [900, 4, 0, 0], "methanol>ethylene_glycol": [1100, 6, 0, 0],
  "ethanol>methanol": [800, 4, 0, 0], "ethanol>ethanol": [500, 2, 0, 0],
  "ethanol>vegetable_oil": [1200, 8, 0, 0], "ethanol>caustic_soda": [3200, 12, 1, 0],
  "ethanol>isopropanol": [900, 4, 0, 0],
  "caustic_soda>caustic_soda": [500, 2, 0, 0], "caustic_soda>methanol": [8500, 24, 1, 0],
  "caustic_soda>ethanol": [8000, 22, 1, 0], "caustic_soda>vegetable_oil": [6000, 18, 1, 0],
  "caustic_soda>palm_oil": [6000, 18, 1, 0], "caustic_soda>toluene": [9000, 28, 1, 0],
  "caustic_soda>xylene": [9000, 28, 1, 0], "caustic_soda>isopropanol": [8000, 22, 1, 0],
  "caustic_soda>phosphoric_acid": [7000, 20, 1, 0],
  "sulphuric_acid>sulphuric_acid": [500, 2, 0, 0], "sulphuric_acid>methanol": [12000, 36, 1, 0],
  "sulphuric_acid>vegetable_oil": [12000, 36, 1, 0], "sulphuric_acid>palm_oil": [12000, 36, 1, 0],
  "sulphuric_acid>ethanol": [12000, 36, 1, 0], "sulphuric_acid>caustic_soda": [15000, 48, 1, 0],
  "sulphuric_acid>toluene": [14000, 42, 1, 0],
  "phosphoric_acid>phosphoric_acid": [500, 2, 0, 0], "phosphoric_acid>methanol": [8000, 24, 1, 0],
  "phosphoric_acid>vegetable_oil": [7500, 22, 1, 0], "phosphoric_acid>caustic_soda": [6000, 18, 1, 0],
  "vegetable_oil>vegetable_oil": [500, 2, 0, 0], "vegetable_oil>palm_oil": [800, 4, 0, 1],
  "vegetable_oil>methanol": [4500, 16, 1, 1], "vegetable_oil>ethanol": [4500, 16, 1, 1],
  "vegetable_oil>caustic_soda": [3000, 10, 1, 1], "vegetable_oil>toluene": [5500, 18, 1, 1],
  "vegetable_oil>isopropanol": [4000, 14, 1, 1],
  "palm_oil>palm_oil": [500, 2, 0, 0], "palm_oil>vegetable_oil": [800, 4, 0, 1],
  "palm_oil>methanol": [4500, 16, 1, 1], "palm_oil>caustic_soda": [3000, 10, 1, 1],
  "palm_oil>toluene": [5500, 18, 1, 1],
  "toluene>toluene": [500, 2, 0, 0], "toluene>xylene": [1000, 4, 0, 0],
  "toluene>methanol": [3000, 10, 1, 0], "toluene>vegetable_oil": [8000, 24, 1, 0],
  "xylene>xylene": [500, 2, 0, 0], "xylene>toluene": [1000, 4, 0, 0],
  "xylene>methanol": [3000, 10, 1, 0],
  "isopropanol>isopropanol": [500, 2, 0, 0], "isopropanol>methanol": [900, 4, 0, 0],
  "isopropanol>ethanol": [900, 4, 0, 0], "isopropanol>vegetable_oil": [3500, 12, 1, 0],
  "ethylene_glycol>ethylene_glycol": [500, 2, 0, 0], "ethylene_glycol>methanol": [2000, 8, 0, 0],
  "ethylene_glycol>vegetable_oil": [3000, 10, 1, 0],
};

const DEFAULT_CLEAN = [6000, 18, 1, 0]; // backend DEFAULT_COST / DEFAULT_HOURS
export const HOURLY_VALUE_USD = 3500;   // backend costs.HOURLY_VALUE_USD

export function cleaningStep(from, to) {
  const raw = from === to ? [500, 2, 0, 0] : M[`${from}>${to}`] || DEFAULT_CLEAN;
  const [chem, hours, wall, heat] = raw;
  return {
    chem, hours,
    wallWash: !!wall,
    heating: !!heat,
    known: from === to || !!M[`${from}>${to}`],
  };
}

// Contamination penalty for a tank residue → new cargo transition.
// reactive / forbidden_same_tank are hard-blocked upstream; here we score the
// residual risk exactly as the backend scorer does for feasible transitions.
export function contaminationPenalty(from, to) {
  const r = relation(from, to);
  if (r === "reactive") return SEVERITY.high * 0.85;
  if (r === "forbidden_same_tank") return SEVERITY.medium * 0.6;
  if (r === "forbidden_adjacent") return SEVERITY.low * 0.15;
  return SEVERITY.low * 0.05; // 750
}

// A transition that may never share a tank (backend forbidden set)
export function forbiddenSameTank(from, to) {
  const r = relation(from, to);
  return r === "reactive" || r === "forbidden_same_tank";
}

// ---- Default vessel: 6 tanks, 3 port/starboard frames ---------------------
export const DEFAULT_TANKS = [
  { id: "1P", cap: 1200, last: "sulphuric_acid" },
  { id: "1S", cap: 1200, last: "methanol" },
  { id: "2P", cap: 1600, last: "caustic_soda" },
  { id: "2S", cap: 1600, last: "ethanol" },
  { id: "3P", cap: 900,  last: "vegetable_oil" },
  { id: "3S", cap: 900,  last: "toluene" },
];

export const ADJACENT = {
  "1P": ["1S", "2P"], "1S": ["1P", "2S"],
  "2P": ["2S", "1P", "3P"], "2S": ["2P", "1S", "3S"],
  "3P": ["3S", "2P"], "3S": ["3P", "2S"],
};

export const DEFAULT_PARCELS = [
  { cargo: "methanol",      vol: 1100 },
  { cargo: "toluene",       vol: 800 },
  { cargo: "vegetable_oil", vol: 1400 },
  { cargo: "caustic_soda",  vol: 850 },
];

// ---- Solver ---------------------------------------------------------------
// Assign each parcel to a distinct tank minimizing total cost, subject to
// capacity, forbidden residue→cargo transitions, and forbidden-adjacent pairs.
// Exhaustive over injective assignments (≤ 6 parcels) → provably optimal.

function pairCost(tank, parcel) {
  if (parcel.vol > tank.cap) return { cost: Infinity, reason: "no_capacity" };
  if (forbiddenSameTank(tank.last, parcel.cargo))
    return { cost: Infinity, reason: "forbidden_transition" };
  const step = cleaningStep(tank.last, parcel.cargo);
  const timeVal = step.hours * HOURLY_VALUE_USD;
  const contam = contaminationPenalty(tank.last, parcel.cargo);
  return {
    cost: step.chem + timeVal + contam,
    step, timeVal, contam,
    reason: null,
  };
}

function adjacencyOk(assignment, tanks, parcels) {
  // assignment: array parcelIdx -> tankIdx
  for (let i = 0; i < assignment.length; i++) {
    for (let j = i + 1; j < assignment.length; j++) {
      if (assignment[i] == null || assignment[j] == null) continue;
      const r = relation(parcels[i].cargo, parcels[j].cargo);
      if (r !== "forbidden_adjacent") continue;
      const ti = tanks[assignment[i]].id, tj = tanks[assignment[j]].id;
      if ((ADJACENT[ti] || []).includes(tj)) return false;
    }
  }
  return true;
}

export function optimize(parcels, tanks) {
  const t0 = (typeof performance !== "undefined" ? performance.now() : Date.now());
  const nP = parcels.length, nT = tanks.length;

  // Precompute pair costs
  const cost = parcels.map((p) => tanks.map((t) => pairCost(t, p)));

  let best = null; // { total, map }
  const used = new Array(nT).fill(false);
  const map = new Array(nP).fill(null);

  function recurse(pi, acc) {
    if (best && acc >= best.total) return; // prune
    if (pi === nP) {
      if (!adjacencyOk(map, tanks, parcels)) return;
      if (!best || acc < best.total) best = { total: acc, map: map.slice() };
      return;
    }
    for (let ti = 0; ti < nT; ti++) {
      if (used[ti]) continue;
      const c = cost[pi][ti].cost;
      if (!isFinite(c)) continue;
      used[ti] = true; map[pi] = ti;
      recurse(pi + 1, acc + c);
      used[ti] = false; map[pi] = null;
    }
  }
  recurse(0, 0);

  const solveMs = Math.max(1, Math.round((typeof performance !== "undefined" ? performance.now() : Date.now()) - t0));

  if (!best) {
    // Diagnose infeasibility per parcel (mirrors backend _diagnose_infeasibility)
    const reasons = parcels.map((p) => {
      const maxCap = Math.max(...tanks.map((t) => t.cap));
      if (p.vol > maxCap * 1.01)
        return { cargo: p.cargo, cause: "no_capacity" };
      const allForbidden = tanks.every((t) => forbiddenSameTank(t.last, p.cargo));
      if (allForbidden) return { cargo: p.cargo, cause: "all_transitions_forbidden" };
      return { cargo: p.cargo, cause: "combined_constraints" };
    });
    return { status: "infeasible", solveMs, reasons };
  }

  // Build per-tank assignment detail
  const assignments = best.map.map((ti, pi) => {
    const t = tanks[ti], p = parcels[pi], pc = cost[pi][ti];
    return {
      tankId: t.id, cap: t.cap, last: t.last,
      cargo: p.cargo, vol: p.vol,
      chem: pc.step.chem, hours: pc.step.hours,
      wallWash: pc.step.wallWash, heating: pc.step.heating,
      timeVal: pc.timeVal, contam: pc.contam,
      total: pc.cost, known: pc.step.known,
    };
  });

  // Naive baseline: positional assignment parcel i -> i-th tank, with violation
  // penalties, to quantify the value of optimizing.
  let naiveTotal = 0, naiveViolations = 0;
  const naiveMap = new Array(nP).fill(null);
  for (let i = 0; i < nP; i++) {
    const ti = i < nT ? i : null;
    naiveMap[i] = ti;
    if (ti == null) { naiveViolations++; naiveTotal += 250000; continue; }
    const pc = cost[i][ti];
    if (!isFinite(pc.cost)) { naiveViolations++; naiveTotal += 250000; }
    else naiveTotal += pc.cost;
  }
  if (!adjacencyOk(naiveMap, tanks, parcels)) { naiveViolations++; naiveTotal += 15000; }

  const optTotal = assignments.reduce((s, a) => s + a.total, 0);
  const totalHours = assignments.reduce((s, a) => s + a.hours, 0);
  const savingsPct = naiveTotal > 0 ? Math.max(0, Math.round((1 - optTotal / naiveTotal) * 100)) : 0;

  return {
    status: "optimal",
    solveMs,
    assignments,
    optTotal,
    totalHours,
    naiveTotal,
    naiveViolations,
    savingsPct,
  };
}
